// Pure helpers — derive KPIs from the leads collection. Used by Dashboard and Reports.

import { STAGES } from './stages';

export function isoWeekKey(d = new Date()) {
  // ISO 8601 week, e.g. "2026-W17"
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

export function startOfWeek(d = new Date()) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // Mon=0
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - day);
  return date;
}
export function endOfWeek(d = new Date()) {
  const s = startOfWeek(d);
  const e = new Date(s); e.setDate(s.getDate() + 7);
  return e;
}

function tsToDate(t) {
  if (!t) return null;
  if (t.toDate) return t.toDate();
  if (t instanceof Date) return t;
  return new Date(t);
}

export function summarize(leads) {
  const totalValue = leads.reduce((s, l) => s + Number(l.estimatedMonthlyValueKES || 0), 0);
  const won = leads.filter(l => l.stage === 'Closed Won');
  const lost = leads.filter(l => l.stage === 'Closed Lost');
  const open = leads.filter(l => !['Closed Won', 'Closed Lost'].includes(l.stage));
  const wonValue = won.reduce((s, l) => s + Number(l.estimatedMonthlyValueKES || 0), 0);

  const byStage = STAGES.map(s => ({
    stage: s,
    count: leads.filter(l => l.stage === s).length,
    valueKES: leads.filter(l => l.stage === s).reduce((sum, l) => sum + Number(l.estimatedMonthlyValueKES || 0), 0)
  }));

  const byOwner = aggregateBy(leads, l => l.ownerName || 'Unassigned');
  const bySource = aggregateBy(leads, l => l.leadSource || 'Unknown');
  const byProduct = aggregateBy(leads, l => l.productInterest || 'Other');

  // Conversion: Closed Won / (Closed Won + Closed Lost), guarded for div-zero
  const closedTotal = won.length + lost.length;
  const winRate = closedTotal === 0 ? 0 : won.length / closedTotal;

  return {
    totalLeads: leads.length, openLeads: open.length, wonCount: won.length, lostCount: lost.length,
    totalPipelineKES: totalValue, wonKES: wonValue, winRate,
    byStage, byOwner, bySource, byProduct
  };
}

function aggregateBy(leads, keyFn) {
  const m = new Map();
  for (const l of leads) {
    const k = keyFn(l);
    const v = Number(l.estimatedMonthlyValueKES || 0);
    const cur = m.get(k) || { key: k, count: 0, valueKES: 0 };
    cur.count += 1;
    cur.valueKES += v;
    m.set(k, cur);
  }
  return Array.from(m.values()).sort((a, b) => b.valueKES - a.valueKES);
}

/**
 * Derive a 12-week KPI grid (mirroring the source Excel scorecard).
 * For each of the last 12 ISO weeks, count leads added in that week, qualified leads, etc.
 */
export function weeklyKpis(leads, { weeks = 12, endDate = new Date() } = {}) {
  // Build week buckets ending with the week containing `endDate`.
  const buckets = [];
  for (let i = weeks - 1; i >= 0; i--) {
    const ref = new Date(endDate); ref.setDate(ref.getDate() - i * 7);
    const start = startOfWeek(ref);
    const end = endOfWeek(ref);
    buckets.push({ key: isoWeekKey(start), start, end, label: `Week of ${start.toISOString().slice(0,10)}` });
  }

  return buckets.map(b => {
    const inWeek = (d) => d && d >= b.start && d < b.end;
    const newProspectsEnriched = leads.filter(l => inWeek(tsToDate(l.dateAdded))).length;
    const qualifiedLeads       = leads.filter(l => l.stage === 'Qualified' && inWeek(tsToDate(l.lastActivityAt))).length;
    const founderPresentations = leads.filter(l => l.stage === 'Presentation' && inWeek(tsToDate(l.lastActivityAt))).length;
    const proposalsSent        = leads.filter(l => l.stage === 'Negotiation' && inWeek(tsToDate(l.lastActivityAt))).length;
    const retainersClosed      = leads.filter(l => l.stage === 'Closed Won' && inWeek(tsToDate(l.lastActivityAt))).length;
    return {
      ...b,
      newProspectsEnriched,
      outreachEmailsSent: 0,        // not derivable from leads; admin can enter manually if/when we add that
      discoveryCallsCompleted: 0,   // ditto
      qualifiedLeads, founderPresentations, proposalsSent, retainersClosed
    };
  });
}
